import { ProspectFinancialReport } from '../fleet-api/services/types';

// Total aset lancar = Kas dan setara kas + piutang usaha pembiayaan asset lancar + investasi aset keuangan lainnya aset lancar + aset lancar lainnya
export const calculateTotalCurrentAsset = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return (
		(values.balanceSheet?.cash ?? 0) +
		(values.balanceSheet?.financingReceivableCurrentAsset ?? 0) +
		(values.balanceSheet?.otherFinancialAssetInvestmentCurrentAsset ?? 0) +
		(values.balanceSheet?.otherCurrentAsset ?? 0)
	);
};

// Total aset tidak lancar = Piutang usaha pembiayaan tidak lancar + investasi aset keuangan lainnya aset tidak lancar + aset tidak lancar lainnya
export const calculateTotalNonCurrentAsset = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return (
		(values.balanceSheet?.financingReceivableNonCurrentAsset ?? 0) +
		(values.balanceSheet?.otherFinancialAssetInvestmentNonCurrentAsset ?? 0) +
		(values.balanceSheet?.otherNonCurrentAsset ?? 0)
	);
};

// Total aset = Total aset lancar + total aset tidak lancar
export const calculateTotalAsset = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return (
		calculateTotalCurrentAsset(values) + calculateTotalNonCurrentAsset(values)
	);
};

// Total liabilitas jangka pendek =	pinjaman jangka pendek + utang usaha jangka pendek + liabilitas jangka pendek lainnya
export const calculateTotalShortTermLiability = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return (
		(values.balanceSheet?.shortTermLoan ?? 0) +
		(values.balanceSheet?.shortTermAccountPayable ?? 0) +
		(values.balanceSheet?.otherShortTermLiability ?? 0)
	);
};

// pinjaman jangka panjang + utang usaha jangka panjang + liabilitas jangka panjang lainnya
export const calculateTotalLongTermLiability = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return (
		(values.balanceSheet?.longTermLoan ?? 0) +
		(values.balanceSheet?.longTermAccountPayable ?? 0) +
		(values.balanceSheet?.otherLongTermLiability ?? 0)
	);
};

// Total Liabilitas = Total Liabilitas jangka pendek + Total liabilitas jangka panjang
export const calculateTotalLiability = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return (
		calculateTotalShortTermLiability(values) +
		calculateTotalLongTermLiability(values)
	);
};

// Equity = Total Aset - Total Liabilitas
export const calculateEquity = (values: ProspectFinancialReport<'payload'>) => {
	return calculateTotalAsset(values) - calculateTotalLiability(values);
};

// Total Liabilitas dan equity = Total Liabilitas + Equity
export const calculateTotalLiabilityAndEquity = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return calculateTotalLiability(values) + (values.balanceSheet?.equity ?? 0);
};

// Current Ratio = Total aset lancar / Total liabilitas jangka pendek
export const calculateCurrentRatio = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return (
		calculateTotalCurrentAsset(values) /
		calculateTotalShortTermLiability(values)
	);
};

// Quick Ratio = (Total aset lancar - persediaan) / Total liabilitas jangka pendek
export const calculateDebtToEquityRatio = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return calculateTotalLiability(values) / (values.balanceSheet?.equity ?? 0);
};

// Debt to Asset Ratio = Total Liabilitas / Total Aset
export const calculateDebtToAssetRatio = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return calculateTotalLiability(values) / calculateTotalAsset(values);
};

// Equity Multiplier = Total Aset / Equity
export const calculateEquityMultiplier = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return calculateTotalAsset(values) / (values.balanceSheet?.equity ?? 0);
};

// Laba Rugi Bruto	= Pendapatan Usaha Operasional - Beban Pokok Pendapatan
export const calculateGrossProfitAndLoss = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return (
		(values.profitAndLoss?.operatingBusinessIncome ?? 0) -
		(values.profitAndLoss?.costOfRevenue ?? 0)
	);
};

// EBITDA	= Laba Rugi Bruto - Biaya Pemasaran Umum dan Administrasi
export const calculateEBITDA = (values: ProspectFinancialReport<'payload'>) => {
	return (
		calculateGrossProfitAndLoss(values) -
		(values.profitAndLoss?.costOfMarketingAndAdmin ?? 0)
	);
};

// Laba Usaha	= EBITDA - Biaya Depresiasi & Amortisasi
export const calculateBusinessProfit = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return (
		calculateEBITDA(values) -
		(values.profitAndLoss?.costOfDepreciationAndAmortization ?? 0)
	);
};

// Laba Rugi Sebelum Pajak	= Laba Usaha + Pendapatan Lain Lain - Beban Lain Lain
export const calculateProfitLossBeforeTax = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return (
		calculateBusinessProfit(values) +
		(values.profitAndLoss?.otherIncome ?? 0) -
		(values.profitAndLoss?.otherExpense ?? 0)
	);
};

// Laba Rugi Tahun Berjalan	=Laba Rugi Sebelum Pajak - Manfaat / Beban Pajak
export const calculateProfitLossCurrentYear = (
	values: ProspectFinancialReport<'payload'>,
) => {
	return (
		calculateProfitLossBeforeTax(values) -
		(values.profitAndLoss?.taxBenefitOrCost ?? 0)
	);
};
